/**
 * The six §1.2 outbox states + their legal transitions (T-03), plus the
 * ack-bound save and the SENDING→QUEUED crash-recovery scan (AC-1g-22
 * acked-never-lost).
 */

import {
  getAllOutboxRecords,
  getOutboxRecord,
  putDeliveredOutboxWithUsageInOneTx,
  putOutboxRecord,
  putOutboxRecordWithBlobsInOneTx,
} from "./db";
import type { QueuedUsageEvent } from "./db";
import type {
  NewCaptureInput,
  OutboxRecord,
  OutboxState,
  RejectionInfo,
} from "./types";
import { getSession } from "../auth/session-store";
import { sessionOwnerPrincipal } from "../auth/workos-session";
import { currentPwaRelease } from "../analytics/release-identity";

/** The §4.7 open-payload-bag locus where `project_ref` rides EVERY emission
 * (AC-1g-07 / TW-1g-PROJECT-INFER) — the same key app.tsx's primary
 * capture/resolve paths and submitFeedback write to. This constant is the
 * single name for that locus so every emitter (primary and secondary)
 * reads/writes the identical field. */
export const PROJECT_REF_KEY = "project_ref";

/**
 * INHERIT the project_ref from a canonical/original record for a SECONDARY
 * re-emission (AC-1g-07 / TW-1g-PROJECT-INFER).
 *
 * A verdict / correction / removal is a fresh Event scoped to the SAME
 * project as the record it references — so its project_ref is not
 * re-selected and NEVER inferred; it is inherited verbatim from the
 * original's `payload.project_ref`. Any post-fix canonical carries a
 * non-empty string here (the primary emitters make it UNCONDITIONAL).
 *
 * Fail-closed: if the original has no non-empty string project_ref, a
 * secondary emission would be silently unscoped — the exact AC-1g-07
 * violation this closes. We throw rather than emit an unscoped Event,
 * mirroring the primary path's `if (project_ref === null) return` refusal.
 */
export function inheritedProjectRef(original: Pick<OutboxRecord, "capture_id" | "envelope">): string {
  const v = original.envelope.payload[PROJECT_REF_KEY];
  if (typeof v !== "string" || v.length === 0) {
    throw new Error(
      `AC-1g-07: cannot re-emit for capture_id "${original.capture_id}" — the canonical record ` +
        `carries no project_ref to inherit; refusing to emit an unscoped Event (never inferred/defaulted).`,
    );
  }
  return v;
}

const ALLOWED: Record<OutboxState, OutboxState[]> = {
  draft: ["held"],
  held: ["queued", "draft"],
  queued: ["sending", "draft"],
  // `sending -> draft` covers the undo-races-delivery window design §1.2
  // describes: a SENDING entry is still undoable until DELIVERED lands.
  sending: ["queued", "delivered", "rejected", "draft"],
  delivered: [],
  rejected: [],
};

export class IllegalTransitionError extends Error {
  constructor(from: OutboxState, to: OutboxState) {
    super(`state-machine: illegal transition ${from} -> ${to}`);
    this.name = "IllegalTransitionError";
  }
}

function assertTransition(from: OutboxState, to: OutboxState): void {
  if (!ALLOWED[from].includes(to)) {
    throw new IllegalTransitionError(from, to);
  }
}

async function requireRecord(capture_id: string): Promise<OutboxRecord> {
  const r = await getOutboxRecord(capture_id);
  if (!r) throw new Error(`state-machine: no outbox record for capture_id "${capture_id}"`);
  return r;
}

/**
 * The single ack-bound write (§1.3): builds the DRAFT→HELD record and
 * commits the outbox row + every blob part in ONE IndexedDB transaction.
 * The caller must await this promise — bound to the transaction's
 * `complete` event, never a network round-trip — before rendering the
 * optimistic ack.
 */
export async function saveToProject(input: NewCaptureInput): Promise<OutboxRecord> {
  const session = getSession();
  const ownerPrincipal = input.owner_principal?.trim()
    || (session ? sessionOwnerPrincipal(session) : `unbound:${input.tenant_id}`);
  const authenticatedOwner = session ? sessionOwnerPrincipal(session) : null;
  const usageActor = session
    && authenticatedOwner === ownerPrincipal
    && session.config.tenant_id === input.tenant_id
    ? session.current_user.user_id.trim()
    : "";
  const parts = input.parts;
  const nowIso = new Date().toISOString();
  const record: OutboxRecord = {
    capture_id: input.capture_id,
    client_id: input.client_id,
    owner_principal: ownerPrincipal,
    ...(usageActor
      ? { usage_actor_id: usageActor, usage_app_release: currentPwaRelease() }
      : {}),
    state: "held",
    createdAt: nowIso,
    heldAt: nowIso,
    retryAt: 0,
    retryCount: 0,
    parts_delivered: {},
    envelope: {
      type: "CaptureLogged",
      schema_version: "2.0.0",
      tenant_id: input.tenant_id,
      capture_id: input.capture_id,
      ts: nowIso,
      skill: input.skill,
      skill_version: input.skill_version,
      source_refs: input.source_refs,
      media: parts
        .slice()
        .sort((a, b) => a.part_index - b.part_index)
        .map((p) => ({
          part_index: p.part_index,
          media_ref: "", // filled in by retry-unit once content-addressed at delivery time (§2.3)
          role: p.role,
          mime: p.mime,
          sha256: p.sha256,
          size: p.size,
        })),
      payload: input.payload ?? {},
      ...(input.timing ? { opened_ts: input.timing.opened_ts, saved_ts: input.timing.saved_ts } : {}),
    },
  };
  await putOutboxRecordWithBlobsInOneTx(record, parts);
  return record;
}

/** HELD -> QUEUED: undo window elapses / user releases (T-14 owns the UI
 * trigger; this is the durable primitive it calls). */
export async function releaseHeld(capture_id: string): Promise<OutboxRecord> {
  const r = await requireRecord(capture_id);
  assertTransition(r.state, "queued");
  r.state = "queued";
  r.retryAt = 0;
  await putOutboxRecord(r);
  return r;
}

/** HELD/QUEUED/SENDING -> DRAFT: undo-last-save (T-14 owns the UI trigger
 * and the negative-probe/toast wiring; this is the durable primitive). */
export async function undoToDraft(capture_id: string): Promise<OutboxRecord> {
  const r = await requireRecord(capture_id);
  assertTransition(r.state, "draft");
  r.state = "draft";
  r.heldAt = undefined;
  await putOutboxRecord(r);
  return r;
}

export async function markSending(capture_id: string): Promise<OutboxRecord> {
  const r = await requireRecord(capture_id);
  assertTransition(r.state, "sending");
  r.state = "sending";
  await putOutboxRecord(r);
  return r;
}

export async function markQueued(
  capture_id: string,
  retryAt: number,
  retryCount: number,
): Promise<OutboxRecord> {
  const r = await requireRecord(capture_id);
  assertTransition(r.state, "queued");
  r.state = "queued";
  r.retryAt = retryAt;
  r.retryCount = retryCount;
  await putOutboxRecord(r);
  return r;
}

export async function markDelivered(capture_id: string): Promise<OutboxRecord> {
  const r = await requireRecord(capture_id);
  assertTransition(r.state, "delivered");
  r.state = "delivered";
  r.deliveredAt = new Date().toISOString();
  await putDeliveredOutboxWithUsageInOneTx(r, captureSyncedEvent(r));
  return r;
}

function captureSyncedEvent(record: OutboxRecord): QueuedUsageEvent | undefined {
  const projectRef = record.envelope.payload[PROJECT_REF_KEY];
  const eventId = `sync:${record.client_id}`;
  if (
    !record.owner_principal
    || !record.usage_actor_id
    || !record.usage_app_release
    || typeof projectRef !== "string"
    || projectRef.length === 0
    || eventId.length > 128
  ) return undefined;
  return {
    owner_principal: record.owner_principal,
    tenant_id: record.envelope.tenant_id,
    actor_id: record.usage_actor_id,
    delivery_state: "pending",
    client_event_id: eventId,
    event_kind: "capture_synced",
    occurred_at: record.deliveredAt!,
    app_release: record.usage_app_release,
    project_ref: projectRef,
  };
}

/** Record that the human has SEEN a refusal, so it stops being shown.
 *
 * Not a transition — `rejected` is terminal and the record stays exactly as it is,
 * including its reason. Idempotent, and a no-op on a record that was never
 * rejected, so a stray call cannot invent an acknowledgement of nothing. */
export async function acknowledgeRejection(capture_id: string): Promise<void> {
  const r = await requireRecord(capture_id);
  if (r.state !== "rejected" || r.rejectionSeenAt) return;
  r.rejectionSeenAt = new Date().toISOString();
  await putOutboxRecord(r);
}

export async function markRejected(capture_id: string, rejection: RejectionInfo): Promise<OutboxRecord> {
  const r = await requireRecord(capture_id);
  assertTransition(r.state, "rejected");
  r.state = "rejected";
  r.rejection = rejection;
  await putOutboxRecord(r);
  return r;
}

/**
 * Startup recovery scan (AC-1g-22): any entry stranded in SENDING (the app
 * was killed mid-flight, so we never learned the outcome) is durably
 * flipped back to QUEUED and made immediately eligible, so the dispatcher
 * (T-05) retries it rather than losing it silently.
 */
export async function recoverOnStartup(owner_principal?: string): Promise<OutboxRecord[]> {
  const all = await getAllOutboxRecords();
  const recovered: OutboxRecord[] = [];
  for (const r of all) {
    if (r.state === "sending" && (!owner_principal || r.owner_principal === owner_principal)) {
      r.state = "queued";
      r.retryAt = 0;
      await putOutboxRecord(r);
      recovered.push(r);
    }
  }
  return recovered;
}
